import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { getSupabase, isSupabaseConfigured } from '../config/supabase.js';
import { StorageManager, isDevMockUserId } from '../storage/index.js';
import { getSyncEngine } from '../services/syncEngine.js';
import { clearAccess } from '../services/access.js';

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const GOOGLE_NOT_ENABLED =
  'Google Sign-In is not enabled yet in your Supabase project. Please enable the Google provider under Supabase Dashboard > Authentication > Providers > Google.';

function getClientKey(profile: any): string | undefined {
  return profile.supabase_publishable_key || profile.supabase_anon_key;
}

/**
 * Developer mock user returned during simulated local development
 */
export const DEV_MOCK_GOOGLE_USER: User = {
  id: 'dev-google-user-0000',
  app_metadata: { provider: 'google', providers: ['google'] },
  user_metadata: {
    full_name: 'Local Developer (Google Auth)',
    name: 'Local Developer',
    avatar_url: 'https://lh3.googleusercontent.com/a/default-user',
    email: 'developer@lingoping.local',
  },
  aud: 'authenticated',
  confirmation_sent_at: '',
  recovery_sent_at: '',
  email_change_sent_at: '',
  new_email: '',
  invited_at: '',
  action_link: '',
  email: 'developer@lingoping.local',
  phone: '',
  created_at: new Date('2026-01-01T00:00:00.000Z').toISOString(),
  confirmed_at: new Date('2026-01-01T00:00:00.000Z').toISOString(),
  email_confirmed_at: new Date('2026-01-01T00:00:00.000Z').toISOString(),
  phone_confirmed_at: '',
  last_sign_in_at: new Date().toISOString(),
  role: 'authenticated',
  updated_at: new Date().toISOString(),
  identities: [],
  factors: [],
};

/**
 * Checks whether the current environment is configured to simulate Google authentication.
 * Returns true if VITE_AUTH_MOCK === 'true' or in development mode without explicit VITE_AUTH_MOCK === 'false'.
 */
export function isDevAuthMock(): boolean {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    // Never simulate sign-in in a staging or production build, even if a shared .env file enables it.
    const buildEnv = import.meta.env.VITE_APP_ENV;
    if (
      import.meta.env.MODE === 'production' ||
      import.meta.env.MODE === 'staging' ||
      buildEnv === 'production' ||
      buildEnv === 'staging'
    ) {
      return false;
    }
    if (import.meta.env.VITE_AUTH_MOCK === 'true') return true;
    if (import.meta.env.VITE_AUTH_MOCK === 'false') return false;
    return import.meta.env.MODE === 'development' || import.meta.env.VITE_APP_ENV === 'development';
  }
  return false;
}

/**
 * Initiates Google OAuth sign-in flow for Chrome extension
 */
export async function signInWithGoogle(): Promise<{
  success: boolean;
  error?: string;
  user?: User;
  /** On a website: the browser is on its way to Google and comes back to this page signed in. */
  redirecting?: boolean;
}> {
  try {
    // 0. If simulated auth is enabled for local development, immediately log in the mock developer user
    if (isDevAuthMock()) {
      console.log(
        '[LingoPing Dev] Simulating Google OAuth Sign-In for local development environment...'
      );
      await StorageManager.setCurrentUserId(DEV_MOCK_GOOGLE_USER.id);
      return { success: true, user: DEV_MOCK_GOOGLE_USER };
    }

    const profile = await StorageManager.getProfile();
    const key = getClientKey(profile);
    const supabase = getSupabase(profile.supabase_url, key);

    if (!isSupabaseConfigured(profile.supabase_url, key)) {
      return {
        success: false,
        error:
          'Supabase credentials not configured yet. Please configure your project URL in Settings or .env.',
      };
    }

    const redirectUrl =
      typeof chrome !== 'undefined' && chrome.identity?.getRedirectURL
        ? chrome.identity.getRedirectURL('supabase-auth')
        : window.location.origin;

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        skipBrowserRedirect: typeof chrome !== 'undefined' && !!chrome.identity?.launchWebAuthFlow,
      },
    });

    if (error) {
      if (
        error.message?.toLowerCase().includes('not enabled') ||
        error.message?.toLowerCase().includes('validation_failed')
      ) {
        return {
          success: false,
          error: GOOGLE_NOT_ENABLED,
        };
      }
      throw error;
    }

    // In Chrome extension, handle auth flow popup via chrome.identity
    if (data?.url && typeof chrome !== 'undefined' && chrome.identity?.launchWebAuthFlow) {
      // Pre-flight check: If provider is not enabled, Supabase endpoint returns HTTP 400 with 'provider is not enabled'
      try {
        const probeRes = await fetch(data.url);
        if (!probeRes.ok) {
          const probeJson = await probeRes.json().catch(() => null);
          const msg = probeJson?.msg || probeJson?.error_description || '';
          if (
            probeJson?.error_code === 'validation_failed' ||
            msg.toLowerCase().includes('not enabled') ||
            probeRes.status === 400
          ) {
            return {
              success: false,
              error: GOOGLE_NOT_ENABLED,
            };
          }
        }
      } catch {
        // Continue to launchWebAuthFlow if network probe is blocked by CORS/fetch
      }

      let responseUrl: string | undefined;
      try {
        responseUrl = await chrome.identity.launchWebAuthFlow({
          url: data.url,
          interactive: true,
        });
      } catch (flowErr: any) {
        const flowMsg = flowErr?.message || '';
        if (
          flowMsg.toLowerCase().includes('authorization page could not be loaded') ||
          flowMsg.toLowerCase().includes('could not be loaded')
        ) {
          return {
            success: false,
            error: GOOGLE_NOT_ENABLED,
          };
        }
        return {
          success: false,
          error: flowMsg || 'Google sign-in popup was closed or blocked.',
        };
      }

      if (!responseUrl) {
        return { success: false, error: 'Authentication was cancelled.' };
      }

      // Extract tokens from response URL hash/query
      const url = new URL(responseUrl);
      const hashParams = new URLSearchParams(url.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      if (accessToken && refreshToken) {
        const { data: sessionData, error: sessionErr } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (sessionErr) throw sessionErr;

        const authUser = sessionData?.user;
        if (authUser) {
          await StorageManager.setCurrentUserId(authUser.id);
          // The first screen should show the account's cards: wait a moment for them. A big
          // account or a slow connection finishes in the background and the page redraws.
          await getSyncEngine().syncNow({ timeoutMs: FIRST_SYNC_WAIT_MS });
          return { success: true, user: authUser };
        }
      }
    }

    // On a website supabase-js has already started navigating to Google (no skipBrowserRedirect).
    if (data?.url && !(typeof chrome !== 'undefined' && chrome.identity?.launchWebAuthFlow)) {
      return { success: false, redirecting: true };
    }

    const current = await getCurrentUser();
    return { success: !!current, user: current || undefined };
  } catch (err: any) {
    console.error('[LingoPing Auth] Sign in with Google error:', err);
    return { success: false, error: err?.message || 'Failed to authenticate with Google' };
  }
}

/** How long signing in waits for the account's data before showing the page. */
const FIRST_SYNC_WAIT_MS = 8000;
/** How long signing out waits for changes that are not saved to the account yet. */
const SIGN_OUT_SEND_WAIT_MS = 5000;

export interface SignOutResult {
  /** False when the sign-out was held back because changes could not be sent (see `unsent`). */
  signedOut: boolean;
  /** Changes of the learner the server has not confirmed. */
  unsent: number;
}

/**
 * Signs out of this device only (signing out of the website must not sign the extension out,
 * and the other way round) and removes the learner's saved data from it.
 *
 * What the learner changed and the server has not confirmed yet is sent first, with a short time
 * limit. If some of it cannot be sent (no connection), nothing is signed out or removed: the
 * answer says how many changes are waiting, and the screen asks the learner whether to lose
 * them. `discardUnsent: true` is their yes.
 */
export async function signOutUser(
  options: { discardUnsent?: boolean } = {}
): Promise<SignOutResult> {
  const userId = await StorageManager.getCurrentUserId();
  const real = !!userId && !isDevMockUserId(userId);
  let unsent = 0;
  if (real) {
    const engine = getSyncEngine();
    await engine.syncNow({ timeoutMs: SIGN_OUT_SEND_WAIT_MS });
    unsent = await engine.pendingCount();
    if (unsent > 0 && !options.discardUnsent) return { signedOut: false, unsent };
  }

  // Forget who is signed in first: an answer still on its way is then thrown away instead of
  // filling the device again.
  await StorageManager.onUserLoggedOut();
  await clearAccess();
  // The simulated development account has no server: its data on the device is all there is.
  if (real) await StorageManager.clearUserData(userId);
  try {
    const profile = await StorageManager.getProfile();
    const key = getClientKey(profile);
    const supabase = getSupabase(profile.supabase_url, key);
    await supabase.auth.signOut({ scope: 'local' });
  } catch (err) {
    console.warn('[LingoPing Auth] Sign out warning:', err);
  }
  return { signedOut: true, unsent };
}

/**
 * Fetches the currently authenticated user and notes who it is. Syncing is not started here:
 * every page starts it when it shows a signed-in learner (`startPageSync`, in AuthGate), the
 * background worker on its own events.
 */
export async function getCurrentUser(): Promise<User | null> {
  const currentUserId = await StorageManager.getCurrentUserId();
  if (isDevAuthMock() && currentUserId === DEV_MOCK_GOOGLE_USER.id) {
    return DEV_MOCK_GOOGLE_USER;
  }

  const profile = await StorageManager.getProfile();
  const key = getClientKey(profile);
  const supabase = getSupabase(profile.supabase_url, key);

  if (!isSupabaseConfigured(profile.supabase_url, key)) {
    return null;
  }

  try {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data?.user) {
      return null;
    }
    await StorageManager.setCurrentUserId(data.user.id);
    return data.user;
  } catch {
    return null;
  }
}

/**
 * Subscribes to Supabase authentication state changes
 */
export function onAuthStateChange(
  callback: (event: AuthChangeEvent, session: Session | null) => void
) {
  const supabase = getSupabase();
  return supabase.auth.onAuthStateChange(callback);
}

/** How long a page waits for the server before it decides whether to show onboarding. */
const ONBOARDING_SYNC_WAIT_MS = 4000;

/**
 * Whether the learner has finished onboarding, on this device or any other (it must not be shown
 * again after a reinstall or on a second device). This device's note of it comes from the account
 * with every sync; a device that has no note asks the server once more before saying no.
 */
export async function isOnboardingCompleteAnywhere(): Promise<boolean> {
  if (await StorageManager.isOnboardingComplete()) return true;

  const userId = await StorageManager.getCurrentUserId();
  if (!userId || isDevMockUserId(userId)) return false;

  await getSyncEngine().syncNow({ timeoutMs: ONBOARDING_SYNC_WAIT_MS });
  return StorageManager.isOnboardingComplete();
}
